import { useState, useEffect, useCallback } from "react";
import { useAutoRefresh } from "../hooks/useAutoRefresh";
import AdminLayout from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import {
  BarChart3,
  TrendingUp,
  Calendar,
  Loader2,
  Download,
  DollarSign,
  Ticket,
  Users,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Reports = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [dailyReport, setDailyReport] = useState([]);
  const [byCashier, setByCashier] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [ownerSearch, setOwnerSearch] = useState("");
  const [dateRange, setDateRange] = useState({
    start_date: "",
    end_date: "",
  });

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/stats`, {
        credentials: "include",
      });
      if (!response.ok) throw new Error("Gagal mengambil statistik");
      const data = await response.json();
      setStats(data);
    } catch (err) {
      toast.error(err.message);
    }
  }, []);

  const fetchDailyReport = useCallback(async (range = {}) => {
    try {
      let url = `${API_URL}/api/reports/daily`;
      const params = new URLSearchParams();
      if (range.start_date) params.append("start_date", range.start_date);
      if (range.end_date) params.append("end_date", range.end_date);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await fetch(url, {
        credentials: "include",
      });
      if (!response.ok) throw new Error("Gagal mengambil laporan harian");
      const data = await response.json();
      setDailyReport(data.report || []);
      setByCashier(data.by_cashier || []);
      setRedemptions(data.redemptions || []);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    Promise.all([fetchStats(), fetchDailyReport()]);
  }, [fetchStats, fetchDailyReport]);
  useAutoRefresh(fetchStats);

  const handleDateFilter = () => {
    setLoading(true);
    fetchDailyReport(dateRange);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("id-ID", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const filteredRedemptions = redemptions.filter((r) => {
    const q = ownerSearch.trim().toLowerCase();
    if (!q) return true;
    return (
      (r.owner_name || "").toLowerCase().includes(q) ||
      (r.voucher_code || "").toLowerCase().includes(q)
    );
  });

  const exportCSV = () => {
    if (dailyReport.length === 0) {
      toast.error("Tidak ada data untuk diekspor");
      return;
    }

    const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const headers = ["Tanggal", "Jumlah Redemption", "Total Nominal (Rp)"];
    const rows = dailyReport.map((r) => [formatDate(r.date), r.count, r.total_nominal]);
    const totalCount = dailyReport.reduce((a, r) => a + r.count, 0);
    const totalNominal = dailyReport.reduce((a, r) => a + r.total_nominal, 0);
    const period = `Periode: ${dateRange.start_date || "30 hari terakhir"}${dateRange.end_date ? ` s/d ${dateRange.end_date}` : ""}`;

    const csvContent = [
      ["Laporan Redemption Voucher"],
      [period],
      [],
      headers,
      ...rows,
      [],
      ["TOTAL", totalCount, totalNominal],
      [],
      ["Laporan Per Kasir"],
      ["Kasir", "Jumlah Voucher", "Total Nominal (Rp)"],
      ...byCashier.map((c) => [c.name, c.count, c.total_nominal]),
      [],
      ["Detail Penukaran"],
      ["Waktu", "Kode Voucher", "Nama Pemilik Voucher", "Nominal (Rp)", "Kasir"],
      ...filteredRedemptions.map((redemption) => [
        new Date(redemption.redeemed_at).toLocaleString("id-ID"),
        redemption.voucher_code,
        redemption.owner_name || "-",
        redemption.nominal,
        redemption.redeemed_by_name,
      ]),
    ]
      .map((row) => row.map(esc).join(";"))
      .join("\r\n");

    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `laporan-redemption-${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
    toast.success("Laporan berhasil diunduh");
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight">Laporan</h1>
            <p className="text-muted-foreground mt-1">
              Statistik dan laporan penggunaan voucher
            </p>
          </div>
          <Button
            variant="outline"
            onClick={exportCSV}
            disabled={dailyReport.length === 0}
            data-testid="btn-export-csv"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>

        {/* Total Nominal Highlight */}
        <Card className="border bg-amber-100" data-testid="total-nominal-card">
          <CardContent className="pt-6 pb-6">
            <div className="flex items-center gap-5">
              <div className="p-4 bg-amber-300 border-2 border-black rounded-2xl">
                <DollarSign className="w-9 h-9 text-black" />
              </div>
              <div>
                <p className="text-sm font-bold uppercase tracking-wider">Total Nominal Voucher Terpakai</p>
                <p className="text-4xl sm:text-5xl font-extrabold leading-tight" data-testid="total-nominal-value">
                  {formatCurrency(stats?.total_nominal_redeemed || 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary Stats */}
        <div className="grid gap-4 sm:grid-cols-3">
          <Card className="border">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-primary/10 rounded-lg">
                  <Ticket className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats?.total || 0}</p>
                  <p className="text-sm text-muted-foreground">Total Voucher</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-emerald-100 rounded-lg">
                  <TrendingUp className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats?.active || 0}</p>
                  <p className="text-sm text-muted-foreground">Voucher Aktif</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <BarChart3 className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats?.redeemed || 0}</p>
                  <p className="text-sm text-muted-foreground">Telah Digunakan</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Today's Highlight */}
        <Card className="border bg-gradient-to-r from-emerald-50 to-emerald-100/50">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-emerald-700">
                  Redemption Hari Ini
                </p>
                <p className="text-4xl font-extrabold text-emerald-800">
                  {stats?.today_redeemed || 0}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">
                  {new Date().toLocaleDateString("id-ID", {
                    weekday: "long",
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Date Filter */}
        <Card className="border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Filter Tanggal
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4 items-end">
              <div className="flex-1 space-y-2">
                <Label htmlFor="start_date">Dari Tanggal</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={dateRange.start_date}
                  onChange={(e) =>
                    setDateRange((prev) => ({
                      ...prev,
                      start_date: e.target.value,
                    }))
                  }
                  data-testid="input-start-date"
                />
              </div>
              <div className="flex-1 space-y-2">
                <Label htmlFor="end_date">Sampai Tanggal</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={dateRange.end_date}
                  onChange={(e) =>
                    setDateRange((prev) => ({
                      ...prev,
                      end_date: e.target.value,
                    }))
                  }
                  data-testid="input-end-date"
                />
              </div>
              <Button
                onClick={handleDateFilter}
                className="min-w-24"
                data-testid="btn-apply-filter"
              >
                Terapkan
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Per Cashier */}
        <Card className="border" data-testid="cashier-report-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Users className="w-5 h-5" /> Laporan Per Kasir</CardTitle>
            <CardDescription>Jumlah voucher dan total nominal yang di-scan tiap kasir pada periode terpilih</CardDescription>
          </CardHeader>
          <CardContent>
            {byCashier.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6" data-testid="cashier-report-empty">Belum ada penukaran pada periode ini</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>#</TableHead>
                      <TableHead>Kasir</TableHead>
                      <TableHead className="text-right">Jumlah Voucher</TableHead>
                      <TableHead className="text-right">Total Nominal</TableHead>
                      <TableHead className="text-right">Terakhir Scan</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {byCashier.map((c, i) => (
                      <TableRow key={c.user_id} data-testid={`cashier-row-${c.user_id}`}>
                        <TableCell className="font-bold">{i + 1}</TableCell>
                        <TableCell className="font-bold">{c.name}</TableCell>
                        <TableCell className="text-right" data-testid={`cashier-count-${c.user_id}`}>{c.count}</TableCell>
                        <TableCell className="text-right font-bold text-emerald-700" data-testid={`cashier-nominal-${c.user_id}`}>{formatCurrency(c.total_nominal)}</TableCell>
                        <TableCell className="text-right text-muted-foreground">
                          {new Date(c.last_redeemed_at).toLocaleString("id-ID", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Daily Report Table */}
        <Card className="border">
          <CardHeader>
            <CardTitle>Laporan Harian</CardTitle>
            <CardDescription>
              Data redemption voucher per hari (30 hari terakhir)
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : dailyReport.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Belum ada data redemption</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tanggal</TableHead>
                      <TableHead className="text-right">Jumlah Redemption</TableHead>
                      <TableHead className="text-right">Total Nominal</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dailyReport.map((row) => (
                      <TableRow key={row.date} data-testid={`report-row-${row.date}`}>
                        <TableCell className="font-medium">
                          {formatDate(row.date)}
                        </TableCell>
                        <TableCell className="text-right">
                          {row.count}
                        </TableCell>
                        <TableCell className="text-right font-medium text-emerald-600">
                          {formatCurrency(row.total_nominal)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Redemption Details */}
        {redemptions.length > 0 && (
          <Card className="border" data-testid="redemption-details-card">
            <CardHeader>
              <CardTitle>Detail Penukaran</CardTitle>
              <CardDescription>
                Nama pemilik tercatat pada laporan dan tidak ditampilkan di voucher.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <Input
                  placeholder="Cari nama pemilik atau kode voucher..."
                  value={ownerSearch}
                  onChange={(e) => setOwnerSearch(e.target.value)}
                  className="h-11 max-w-md"
                  data-testid="input-search-owner"
                />
                {ownerSearch && (
                  <p className="text-xs text-muted-foreground mt-2" data-testid="search-owner-summary">
                    {filteredRedemptions.length} dari {redemptions.length} penukaran cocok
                  </p>
                )}
              </div>
              <div className="overflow-x-auto">
                <Table data-testid="redemption-details-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Kode Voucher</TableHead>
                      <TableHead>Nama Pemilik</TableHead>
                      <TableHead>Nominal</TableHead>
                      <TableHead>Kasir</TableHead>
                      <TableHead>Waktu</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRedemptions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground py-6" data-testid="redemption-detail-empty">
                          Tidak ada penukaran yang cocok
                        </TableCell>
                      </TableRow>
                    ) : filteredRedemptions.map((redemption, index) => (
                      <TableRow
                        key={`${redemption.voucher_code}-${redemption.redeemed_at}`}
                        data-testid={`redemption-detail-row-${index}`}
                      >
                        <TableCell
                          className="font-mono font-medium"
                          data-testid={`redemption-detail-code-${index}`}
                        >
                          {redemption.voucher_code}
                        </TableCell>
                        <TableCell data-testid={`redemption-detail-owner-${index}`}>
                          {redemption.owner_name || "-"}
                        </TableCell>
                        <TableCell className="text-emerald-600 font-medium">
                          {formatCurrency(redemption.nominal)}
                        </TableCell>
                        <TableCell data-testid={`redemption-detail-cashier-${index}`}>
                          {redemption.redeemed_by_name}
                        </TableCell>
                        <TableCell
                          className="text-muted-foreground"
                          data-testid={`redemption-detail-time-${index}`}
                        >
                          {new Date(redemption.redeemed_at).toLocaleString("id-ID")}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default Reports;
